import { useState } from "react";
import type { ThemeClasses } from "../../../types/components";
import type { CrudField, CrudRow } from "../types";

// Renders the add/edit form from a section's field schema and returns the
// collected values on submit. Pure presentational + local form state.

interface EntityFormProps {
  fields: CrudField[];
  initial?: Partial<CrudRow>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

export default function EntityForm({
  fields,
  initial,
  themeClasses,
  isDarkMode,
  submitLabel,
  onCancel,
  onSubmit,
}: EntityFormProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    for (const f of fields) {
      if (f.type === "info") continue;
      const seed = initial?.[f.key] ?? f.defaultValue ?? "";
      base[f.key] = String(seed);
    }
    return base;
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const inputBase = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  const set = (key: string, v: string) =>
    setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = () => {
    const nextErrors: Record<string, boolean> = {};
    for (const f of fields) {
      if (f.required && !values[f.key]?.trim()) nextErrors[f.key] = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(values);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "info" ? "sm:col-span-2" : ""}>
            <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
              {f.label}
              {f.required && <span className="ml-0.5 text-red-500">*</span>}
            </label>

            {f.type === "info" ? (
              f.render?.(initial)
            ) : f.type === "select" ? (
              <select
                value={values[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              >
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                rows={3}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            ) : (
              <input
                type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text"}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            )}

            {errors[f.key] && (
              <p className="mt-1 text-xs text-red-500">Este campo es obligatorio</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-400"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
